// @owners { team: patients }
import { SIZES } from '@alto/design-library-tokens';
import { Border, Button, Column, LgSpacing, MdSpacing, Pressable, Row } from '@alto/design-system';
import { useAddOnsContext, useQueryEssentials } from '@alto/features';
import { useNavigation } from '@alto/navigation';
import React from 'react';
import { getShipmentWithID } from '~shared/features/shipments/selectors/getShipments';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useSelectorShared } from '~shared/store';
import { ErrorPage, LoadingPage } from '~web/features/essentials/components';
import { useTrackViewedEvent } from '~web/features/essentials/hooks';
import { ProductCarousel } from '~web/features/essentials/pages/OrderSuccess/ProductCarousel';
import { Icon, Image, Text } from '~web/features/essentials/primitives';
import { useDeprecatedMediaQuery } from '~web/hooks';

export const DesktopProducts = () => {
  const { navigate } = useNavigation();
  const { shipmentID, origin } = useAddOnsContext();
  const shipment = useSelectorShared((state) => getShipmentWithID(state, { shipmentID }));
  const { isLoading, error, categories } = useQueryEssentials({
    addressID: shipment?.address_id,
    date: shipment?.scheduled_date,
  });
  useTrackViewedEvent(EVENTS.ESSENTIALS__ORDER_SUCCESS_WITH_ESSENTIALS, shipmentID, origin);
  const isMediumScreen = useDeprecatedMediaQuery('MD');
  const numItems = isMediumScreen ? 4 : 2;

  if (isLoading || !categories) return <LoadingPage />;
  if (error)
    return (
      <ErrorPage
        title="Unable to load Essentials"
        description={`${error}`}
      />
    );
  if (categories.length === 0) return null;

  const handleNavToEssentials = () => {
    navigate('RouteEssentials', { search: `?order=${shipmentID}&origin=${origin}` });
  };

  return (
    <>
      <Row
        spaceBetween
        centerVertically
      >
        <Column>
          <Row centerVertically>
            <Text
              title="Add "
              variant="h2"
            />
            <Image
              src="https://assets.prod.alto.com/otc_categories/essentials_logo.svg"
              alt="add essentials to your order"
            />
          </Row>
          <Text
            title=" to your order"
            variant="h2"
          />
        </Column>
        {shipmentID ? (
          <Button
            label="See all products"
            onPress={handleNavToEssentials}
          />
        ) : null}
      </Row>
      <LgSpacing />
      <Border fullWidth />
      <LgSpacing />
      {shipmentID &&
        categories.map((category) => (
          <Column key={category.name}>
            <Pressable onPress={handleNavToEssentials}>
              <Row centerVertically>
                <Image
                  src={category.image_url}
                  width={SIZES.ILLUSTRATION.MD.px}
                  height={SIZES.ILLUSTRATION.MD.px}
                  alt={`icon for ${category.name}`}
                />
                <MdSpacing />
                <Text
                  title={category.name}
                  variant="h3"
                />
                <MdSpacing />
                <Icon
                  name="chevronright"
                  size="SM"
                  interactive
                  label="back icon"
                />
              </Row>
            </Pressable>
            <ProductCarousel
              numOfItems={category.add_on_otcs.length}
              numOfColumns={numItems}
              products={category.add_on_otcs}
              categoryName={category.name}
            />
            <LgSpacing />
          </Column>
        ))}
    </>
  );
};
